import type { Cell, Header, Table } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/ui/accordion";

import { Skeleton } from "../skeleton";

import type { VirtualizedTableProps } from "@/shared/types";
import { Checkbox } from "@/shared/ui/checkbox.tsx";
import { ActiveFilters } from "@/features/ActiveFilters";

export function VirtualizedMobileDataTable<TData, TValue>({
  data,
  isLoading,
  table,
  selectMobileColumn,
  refetch,
}: Omit<VirtualizedTableProps<TData, TValue>, "columns"> & {
  table: Table<TData>;
}) {
  const allRowsSelected = table.getIsAllRowsSelected();
  if (data && data.length === 0) {
    return (
      <div className="py-4 text-center text-gray-500" role="status">
        {isLoading ? <Skeleton /> : <span>Ничего нет.</span>}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {refetch && <ActiveFilters refetch={refetch} />}
      {isLoading ? (
        <div className="space-y-2" role="status" aria-label="Loading data">
          {Array.from({ length: 9 }, (_, index) => (
            <div
              key={index}
              className="h-20 animate-pulse rounded-md bg-gray-200"
            />
          ))}
        </div>
      ) : (
        <Accordion type="single" collapsible className="space-y-2">
          <div className="align-center flex items-center gap-2">
            <Checkbox
              onClick={() => table.toggleAllRowsSelected(!allRowsSelected)}
              checked={allRowsSelected}
              aria-label="Select all rows"
            />
            <p>Выбрать всех</p>
          </div>
          {table.getRowModel().rows.map((row) => (
            <MobileDataTableItem
              key={row.id}
              id={row.id}
              headers={table.getHeaderGroups()[0].headers}
              rows={row.getVisibleCells()}
              tableHeader={row.getVisibleCells()[selectMobileColumn ?? 1]}
            />
          ))}
        </Accordion>
      )}
    </div>
  );
}

interface MobileDataTableItemProps<TData> {
  headers: Header<TData, unknown>[];
  rows: Cell<TData, unknown>[];
  tableHeader: Cell<TData, unknown>;
  id: string;
}

const MobileDataTableItem = ({
  headers,
  rows,
  id,
  tableHeader,
}: MobileDataTableItemProps<any>) => {
  const zip = <T, U>(a: T[], b: U[]) => a.map((k, i) => [k, b[i]] as [T, U]);

  return (
    <AccordionItem
      value={`item-${id}`}
      className="rounded border-b border-white/15 px-2 shadow backdrop-blur-md"
    >
      <AccordionTrigger className="flex w-full items-center justify-between py-4">
        <div className="flex w-full items-center justify-between gap-3">
          <div className="flex max-w-[250px] items-start justify-between self-center overflow-hidden text-ellipsis whitespace-nowrap text-textColor">
            {flexRender(
              tableHeader.column.columnDef.cell,
              tableHeader.getContext(),
            )}
          </div>
        </div>
      </AccordionTrigger>

      <AccordionContent>
        <div className="space-y-3 rounded-lg bg-bgColor">
          {zip(rows, headers).map(([row, header]) => (
            <div
              key={row.id}
              className="flex w-full items-center justify-between px-4 py-3 text-textColor"
            >
              <div>
                {header.column.id !== "select" &&
                  flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
              </div>
              <div>
                {flexRender(row.column.columnDef.cell, row.getContext())}
              </div>
            </div>
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
